import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Box, Typography, Grid } from "@mui/material";
import { User } from "@/interfaces/User";
import { IconX } from "@tabler/icons-react";
import { Button } from "./ui/button";
import { useAppDispatch } from "@/redux/hooks";
import { updateSelf, getUser } from "@/redux/user/user.action";
import { Input } from "./ui/input";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { SuccessToast } from "@/utilities/toasts/SuccessToast";
import { ErrorToast } from "@/utilities/toasts/ErrorToast";

interface DialogProfileProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  userInfo: User;
}

const DialogProfile = ({ open, setOpen, userInfo }: DialogProfileProps) => {
  const dispatch = useAppDispatch();
  const [editVariable, setEditVariable] = useState<boolean>(false);
  const [variableValue, setVariableValue] = useState<Partial<User>>({
    username: userInfo.username,
    email: userInfo.email,
    role: userInfo.role,
    company: userInfo.company,
    position: userInfo.position,
  });

  useEffect(() => {
    setEditVariable(false);
  }, [userInfo]);

  const handleEditVariable = (value: boolean) => {
    setEditVariable(value);
  };

  const handleEditVariableChange = async () => {
    const response = await dispatch(updateSelf(variableValue));
    if (response.type.includes("fulfilled")) {
      setEditVariable(false);
      await dispatch(getUser());
      SuccessToast({ message: "Perfil actualizado correctamente" });
      setOpen((prev) => !prev);
    } else {
      ErrorToast({ message: "Error al actualizar el perfil" });
      setEditVariable(false);
      setOpen((prev) => !prev);
    }
  };

  const handleUpdateSelf = (key: string, value: string) => {
    setVariableValue((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <VisuallyHidden>
          <DialogTitle>UserConfigs</DialogTitle>
        </VisuallyHidden>
        <DialogContent
          className="bg-sidebar min-w-[15rem] w-[30rem]"
          aria-describedby={undefined}
        >
          <DialogHeader className="flex pb-0 h-fit gap-1">
            <Box className="flex justify-end w-full font-bold">
              <Button
                className="p-0 text-[#1F6B45] h-fit"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <IconX className="p-0" />
              </Button>
            </Box>
            <DialogTitle>
              <Typography
                fontFamily={"Inter"}
                fontSize={"18px"}
                fontWeight={500}
                letterSpacing={0}
              >
                Configuración de usuario
              </Typography>
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Consulta y edita la información del perfil del usuario
              seleccionado.
            </DialogDescription>
          </DialogHeader>
          <Grid container gap={1} className="w-full flex justify-between">
            <Grid item xs={5.5}>
              <Typography
                fontFamily={"Inter"}
                fontSize={"14px"}
                fontWeight={500}
                letterSpacing={0}
              >
                Usuario:
              </Typography>
              <Input
                value={variableValue.username}
                onChange={(e) => handleUpdateSelf("username", e.target.value)}
                className="w-full mt-1"
                disabled={!editVariable}
              />
            </Grid>
            <Grid item xs={5.5}>
              <Typography
                fontFamily={"Inter"}
                fontSize={"14px"}
                fontWeight={500}
                letterSpacing={0}
              >
                Correo:
              </Typography>

              <Input
                value={variableValue.email}
                onChange={(e) => handleUpdateSelf("email", e.target.value)}
                className="w-full mt-1"
                disabled={!editVariable}
              />
            </Grid>
            <Grid item xs={5.5}>
              <Typography
                fontFamily={"Inter"}
                fontSize={"14px"}
                fontWeight={500}
                letterSpacing={0}
              >
                Rol:
              </Typography>
              <Input
                value={variableValue.role}
                onChange={(e) => handleUpdateSelf("role", e.target.value)}
                className="w-full mt-1"
                disabled={true}
              />
            </Grid>
            <Grid item xs={5.5}>
              <Typography
                fontFamily={"Inter"}
                fontSize={"14px"}
                fontWeight={500}
                letterSpacing={0}
              >
                Cargo:
              </Typography>
              <Input
                value={
                  variableValue.position
                    ? variableValue.position
                    : "No definido"
                }
                onChange={(e) => handleUpdateSelf("position", e.target.value)}
                className="w-full mt-1"
                disabled={true}
              />
            </Grid>
            <Grid item xs={12} className="w-full">
              <Typography
                fontFamily={"Inter"}
                fontSize={"14px"}
                fontWeight={500}
                letterSpacing={0}
              >
                Empresa:
              </Typography>
              <Input
                value={variableValue.company}
                onChange={(e) => handleUpdateSelf("company", e.target.value)}
                className="w-full mt-1"
                disabled={true}
              />
            </Grid>
          </Grid>
          <DialogFooter
            className={`flex w-full gap-4 pt-4 flex-row items-center justify-center flex-wrap sm:flex-row sm:justify-end sm:items-center`}
          >
            {editVariable && (
              <Button
                variant="cancel"
                className="w-full justify-self-start w-[7.5rem] flex"
                onClick={() => handleEditVariable(false)}
              >
                <Typography
                  fontFamily={"Inter"}
                  fontSize={"14px"}
                  fontWeight={500}
                >
                  Cancelar
                </Typography>
              </Button>
            )}
            {userInfo.role === "Administrador" && (
              <Button
                variant="contained"
                className="w-full justify-self-end w-[7.5rem] flex hover:bg-[#1F6B45]/90 shadow-sm"
                onClick={() =>
                  editVariable
                    ? handleEditVariableChange()
                    : handleEditVariable(true)
                }
              >
                <Typography
                  fontFamily={"Inter"}
                  fontSize={"14px"}
                  fontWeight={500}
                >
                  {editVariable ? "Aceptar" : "Editar"}
                </Typography>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DialogProfile;
